import {
  useFetchers,
  useLocation,
  useNavigation,
  type Fetcher,
} from "react-router-dom";

/**
 * Centralizes React Router pending state for loaders, actions, and fetchers.
 *
 * - `showLayoutOverlay`: cross-route only (dim + TopProgressBar). Not used on
 *   initial load or filter/pagination navigations.
 * - `showContentFallback`: child route loading (initial entry, hard refresh).
 *   Excludes query-only navigations so tables keep `isTableLoading` behavior.
 * - `isTableLoading`: same pathname, query/hash change (filters, pagination).
 */
export function useRouteActivity() {
  const navigation = useNavigation();
  const location = useLocation();
  const fetchers = useFetchers();

  const isNavigating = navigation.state === "loading";
  const isSubmitting = navigation.state === "submitting";
  const pendingLocation = navigation.location;

  const isCrossRoute =
    pendingLocation != null &&
    pendingLocation.pathname !== location.pathname;

  /** Filter/pagination: same path, different search or hash (not initial load). */
  const isQueryOnlyNavigation =
    pendingLocation != null &&
    pendingLocation.pathname === location.pathname &&
    (pendingLocation.search !== location.search ||
      pendingLocation.hash !== location.hash);

  const isFetcherBusy = fetchers.some((fetcher) => fetcher.state !== "idle");

  const showLayoutOverlay = (isNavigating || isSubmitting) && isCrossRoute;

  const showContentFallback =
    isNavigating && !isCrossRoute && !isQueryOnlyNavigation;

  /** Same-route loader refresh (filters, query). Cross-route uses layout overlay. */
  const isTableLoading = isNavigating && !isCrossRoute;

  return {
    isNavigating,
    isSubmitting,
    isCrossRoute,
    isQueryOnlyNavigation,
    isFetcherBusy,
    isPending: isNavigating || isSubmitting || isFetcherBusy,
    isTableLoading,
    isFormBusy: isSubmitting || isFetcherBusy,
    showLayoutOverlay,
    showContentFallback,
  };
}

/** True while a specific fetcher is submitting or loading its response. */
export function useFetcherBusy(fetcher: Pick<Fetcher, "state">) {
  return fetcher.state !== "idle";
}

/** Use for Layer 4 banner: long server work (reports, bulk access). Same as fetcher busy. */
export function useLongOperationBusy(fetcher: Pick<Fetcher, "state">) {
  return useFetcherBusy(fetcher);
}
